import { token } from '../theme/tokens.js';

/**
 * A glyph for a file's eight-value `kind` taxonomy (FIL-4) — derived from
 * MIME/extension at upload, informational rather than behavioral, shown
 * beside a file's name the way `AgencyTypeIcon` sits beside an agency's.
 *
 * **Promoted here rather than hand-copied per call site**, on `AgencyTypeIcon`'s
 * own precedent (ADR-0062): `FolderView`'s file table and card both drew it
 * locally until the recycle bin (FIL-11) became a second consumer, past the
 * point duplicating eight glyphs stays idiomatic.
 *
 * Every path is Lucide, hand-copied like every other icon in this workspace
 * (no `lucide-react` dependency, ADR-0053's reasoning). The house SVG contract
 * — `viewBox 0 0 24 24`, `fill="none"`, `stroke="currentColor"`, 2px round
 * caps/joins — is `AgencyTypeIcon`'s, unchanged.
 */

export type FileKind =
  'PDF' | 'VIDEO' | 'PHOTOS' | 'AUDIO' | 'ARCHIVE' | 'PHONE_DUMP' | 'DOC' | 'OTHER';

export interface FileKindIconProps {
  readonly kind: FileKind;
  /**
   * The accessible name. **Required, not optional** — this package hardcodes
   * no English (see `packages/ui/CLAUDE.md`), so the label naming what kind
   * of file this is is always the caller's own copy.
   */
  readonly label: string;
  readonly className?: string;
}

function PdfGlyph() {
  return (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </>
  );
}

function VideoGlyph() {
  return (
    <>
      <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </>
  );
}

function PhotosGlyph() {
  return (
    <>
      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </>
  );
}

function AudioGlyph() {
  return (
    <>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </>
  );
}

function ArchiveGlyph() {
  return (
    <>
      <rect width="20" height="5" x="2" y="3" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </>
  );
}

function PhoneDumpGlyph() {
  return (
    <>
      <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
      <path d="M12 18h.01" />
    </>
  );
}

function DocGlyph() {
  return (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </>
  );
}

function OtherGlyph() {
  return (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </>
  );
}

const GLYPH: Record<FileKind, () => React.JSX.Element> = {
  PDF: PdfGlyph,
  VIDEO: VideoGlyph,
  PHOTOS: PhotosGlyph,
  AUDIO: AudioGlyph,
  ARCHIVE: ArchiveGlyph,
  PHONE_DUMP: PhoneDumpGlyph,
  DOC: DocGlyph,
  OTHER: OtherGlyph,
};

export function FileKindIcon({ kind, label, className }: FileKindIconProps) {
  const Glyph = GLYPH[kind];

  return (
    <span role="img" aria-label={label} className={className}>
      <svg
        viewBox="0 0 24 24"
        width={18}
        height={18}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="shrink-0"
        style={{ color: token('bp-text-secondary') }}
      >
        <Glyph />
      </svg>
    </span>
  );
}
